import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Fuel,
  Heart,
  Phone,
  Settings,
  Star,
} from "lucide-react";
import { formatMileage, formatPrice } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogDescription,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Label } from "../ui/label";
import { Car, CarCreate } from "../../types";
import { useState } from "react";
import { useRole } from "../../context/RoleContext";
import { STAFF_ROLES, hasRole } from "../../utils/general";
import VehicleExpensesPanel from "../organisms/VehicleExpensesPanel";

export interface CarDetailsModalProps {
  selectedCar: Car | CarCreate;
  setSelectedCar: React.Dispatch<React.SetStateAction<Car | CarCreate | null>>;
  setShowInquiryForm?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CarDetailsModal: React.FC<CarDetailsModalProps> = ({
  selectedCar,
  setSelectedCar,
  setShowInquiryForm,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const isStaff = hasRole(useRole(), STAFF_ROLES);

  const nextImage = (): void => {
    if (selectedCar && selectedCar.images.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === selectedCar.images.length - 1 ? 0 : prev + 1,
      );
    }
  };

  const prevImage = (): void => {
    if (selectedCar && selectedCar.images.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedCar.images.length - 1 : prev - 1,
      );
    }
  };
  return (
    <Dialog open={!!selectedCar} onOpenChange={() => setSelectedCar(null)}>
      <DialogContent className="w-[95%] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {selectedCar.year} {selectedCar.make} {selectedCar.model}
          </DialogTitle>
          <DialogDescription className="text-xl font-semibold text-blue-600">
            {formatPrice(selectedCar.price)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image Gallery */}
          <div className="relative w-full">
            <img
              src={selectedCar.images[currentImageIndex]}
              alt={`${selectedCar.make} ${selectedCar.model}`}
              className="w-full h-full object-cover rounded-lg"
            />
            {selectedCar.images.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white/80 backdrop-blur-sm"
                  onClick={prevImage}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white/80 backdrop-blur-sm"
                  onClick={nextImage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          <Tabs defaultValue="details" className="w-full">
            <TabsList
              className={`grid w-full ${isStaff ? "grid-cols-3" : "grid-cols-2"}`}
            >
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              {isStaff && (
                <TabsTrigger value="expenses">Expenses & Profit</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Calendar className="mx-auto h-6 w-6 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-600">Mileage</p>
                  <p className="font-semibold">
                    {formatMileage(selectedCar.mileage)} km
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Fuel className="mx-auto h-6 w-6 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-600">Fuel Type</p>
                  <p className="font-semibold">{selectedCar.fuel_type}</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Settings className="mx-auto h-6 w-6 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-600">Transmission</p>
                  <p className="font-semibold">{selectedCar.transmission}</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Star className="mx-auto h-6 w-6 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-600">Power</p>
                  <p className="font-semibold">{selectedCar.horsepower} HP</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-gray-700">{selectedCar.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Color</Label>
                  <p className="font-medium">{selectedCar.color}</p>
                </div>
                <div>
                  <Label>Engine</Label>
                  <p className="font-medium">{selectedCar.cubic_capacity}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="features">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedCar.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center p-2 bg-gray-50 rounded"
                  >
                    <Star className="h-4 w-4 text-green-600 mr-2" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            {isStaff && (
              <TabsContent value="expenses">
                <VehicleExpensesPanel vehicleId={selectedCar.id} />
              </TabsContent>
            )}
          </Tabs>

          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => setShowInquiryForm?.(true)}
              className="flex-1 min-w-[140px]"
            >
              <Phone className="mr-2 h-4 w-4" />
              Make an Inquiry
            </Button>
            <Button variant="outline" className="flex-1 min-w-[140px]">
              <Heart className="mr-2 h-4 w-4" />
              Save to Favorites
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CarDetailsModal;
